import { ReactNode, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';

type FieldConfig = {
  required?: boolean;
  weight?: number;
  isFilled?: (value: any) => boolean;
};

type FieldConfigMap = {
  [key: string]: FieldConfig | FieldConfigMap;
};

type QualityStatusConfig = {
  label: string;
  type: 'success-light' | 'warning' | 'error';
  icon: ReactNode;
};

export const useQualityStatus = <T extends object>(fieldPath: string, fieldConfig: FieldConfig | FieldConfigMap) => {
  const { watch } = useFormContext<T>();
  const { dictionary } = useContext(LocalizationContext);

  const completionPercentage = useMemo(() => {
    const watchField = (
      path: string,
      config: FieldConfig | FieldConfigMap,
    ): { value: any; weight: number; isFilled?: FieldConfig['isFilled'] }[] => {
      if ('required' in config || 'weight' in config || 'isFilled' in config) {
        const leafConfig = config as FieldConfig;
        const value = watch(path);
        return [{ value, weight: leafConfig.weight || 1, isFilled: leafConfig.isFilled }];
      }

      return Object.entries(config).flatMap(([key, subConfig]) => {
        const fullPath = path ? `${path}.${key}` : key;
        return watchField(fullPath, subConfig);
      });
    };

    const fields = watchField(fieldPath, fieldConfig);
    const totalWeight = fields.reduce((sum, field) => sum + field.weight, 0);
    const filledWeight = fields.reduce((sum, field) => {
      const isFilled = field.isFilled
        ? field.isFilled(field.value)
        : Array.isArray(field.value)
          ? field.value.length > 0
          : Boolean(field.value);
      return sum + (isFilled ? field.weight : 0);
    }, 0);

    return Math.round((filledWeight / totalWeight) * 100);
  }, [watch, fieldPath, fieldConfig]);

  const getStatusConfig = (): QualityStatusConfig => {
    if (completionPercentage >= 80) {
      return {
        label: dictionary.goodStatus,
        type: 'success-light',
        icon: undefined,
      };
    } else if (completionPercentage >= 60) {
      return {
        label: dictionary.mediumQuality,
        type: 'warning',
        icon: undefined,
      };
    } else {
      return {
        label: dictionary.lowQuality,
        type: 'error',
        icon: undefined,
      };
    }
  };

  return {
    completionPercentage,
    statusConfig: getStatusConfig(),
  };
};
